/* =========================================================
   KORU CORE HOMEPAGE — ZAETH
   Cinematic loader / reveal / parallax / audio / counter
   ========================================================= */

(function () {
  "use strict";

  const root = document.querySelector("[data-koru-core]");
  if (!root) return;

  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  const isEditor = document.documentElement.classList.contains(
    "shopify-design-mode"
  );

  const loader = root.querySelector("[data-koru-loader]");
  const audio = root.querySelector("[data-koru-audio]");

  const duration =
    Math.max(
      5,
      Math.min(20, Number(root.dataset.audioDuration || 10))
    ) * 1000;

  let audioTimer = null;
  let audioStarted = false;
  let raf = null;

  /* =========================================================
     HELPERS
     ========================================================= */

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  /* =========================================================
     CINEMATIC LOADER
     ========================================================= */

  function revealLoader() {
    if (!loader || isEditor) {
      document.body.classList.add("koru-ready");
      return;
    }

    window.setTimeout(function () {
      loader.classList.add("is-done");
      document.body.classList.add("koru-ready");
    }, reduceMotion ? 150 : 2500);
  }

  /* =========================================================
     HERO INTRO
     ========================================================= */

  function prepareHero() {
    const hero = root.querySelector(".koru-hero");

    if (!hero) return;

    window.requestAnimationFrame(function () {
      hero.classList.add("is-ready");
    });
  }

  /* =========================================================
     SECTION REVEALS
     ========================================================= */

  function setupReveal() {
    const sections = root.querySelectorAll(
      ".koru-reveal-section"
    );

    if (
      reduceMotion ||
      isEditor ||
      !("IntersectionObserver" in window)
    ) {
      sections.forEach(function (section) {
        section.classList.add("is-visible");
      });

      return;
    }

    const observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          entry.target.classList.add("is-visible");

          obs.unobserve(entry.target);
        });
      },
      {
        threshold: 0.08,
        rootMargin: "0px 0px -8% 0px"
      }
    );

    sections.forEach(function (section) {
      observer.observe(section);
    });
  }

  /* =========================================================
     CINEMATIC PARALLAX
     ========================================================= */

  function setupParallax() {
    if (reduceMotion) return;

    const elements = root.querySelectorAll(
      "[data-koru-parallax]"
    );

    if (!elements.length) return;

    const update = function () {
      elements.forEach(function (element) {
        const speed = Number(
          element.dataset.koruParallax || 0.1
        );

        const rect = element.getBoundingClientRect();

        const center =
          rect.top + rect.height / 2;

        const viewportCenter =
          window.innerHeight / 2;

        const distance =
          center - viewportCenter;

        const movement = clamp(
          distance * speed * -0.08,
          -28,
          28
        );

        element.style.transform =
          "translate3d(0," +
          movement.toFixed(2) +
          "px,0)";
      });

      raf = null;
    };

    const requestUpdate = function () {
      if (raf) return;

      raf = window.requestAnimationFrame(
        update
      );
    };

    window.addEventListener(
      "scroll",
      requestUpdate,
      {
        passive: true
      }
    );

    window.addEventListener(
      "resize",
      requestUpdate,
      {
        passive: true
      }
    );

    requestUpdate();
  }

  /* =========================================================
     FIRST 100 COUNTER
     ========================================================= */

  function setupCounter() {
    const counter = root.querySelector(
      "[data-koru-counter]"
    );

    if (!counter) return;

    const target = Number(
      counter.dataset.koruCounter || 0
    );

    if (reduceMotion) {
      counter.textContent = target;
      return;
    }

    let done = false;

    const animate = function () {
      if (done) return;

      done = true;

      const start = performance.now();
      const length = 1200;

      function frame(now) {
        const progress = clamp(
          (now - start) / length,
          0,
          1
        );

        const eased =
          1 - Math.pow(1 - progress, 4);

        counter.textContent =
          Math.round(target * eased);

        if (progress < 1) {
          requestAnimationFrame(frame);
        } else {
          counter.textContent = target;
        }
      }

      requestAnimationFrame(frame);
    };

    if ("IntersectionObserver" in window) {
      const observer =
        new IntersectionObserver(
          function (entries, obs) {
            if (
              entries.some(function (entry) {
                return entry.isIntersecting;
              })
            ) {
              animate();
              obs.disconnect();
            }
          },
          {
            threshold: 0.35
          }
        );

      observer.observe(counter);
    } else {
      animate();
    }
  }

  /* =========================================================
     OPENING AUDIO
     ========================================================= */

  function startAudio() {
    if (!audio) return;

    if (
      root.dataset.audioEnabled !== "true"
    ) {
      return;
    }

    if (audioStarted) return;

    audioStarted = true;

    const volume = clamp(
      Number(
        root.dataset.audioVolume || 0.08
      ),
      0.01,
      0.25
    );

    audio.volume = volume;

    audio.currentTime = 0;

    const playPromise = audio.play();

    if (
      playPromise &&
      typeof playPromise.catch === "function"
    ) {
      playPromise.catch(function () {
        audioStarted = false;
      });
    }

    window.clearTimeout(audioTimer);

    audioTimer = window.setTimeout(
      function () {
        audio.pause();

        try {
          audio.currentTime = 0;
        } catch (e) {}
      },
      duration
    );
  }

  function setupAudio() {
    if (!audio) return;

    if (
      root.dataset.audioEnabled !== "true"
    ) {
      return;
    }

    if (isEditor) return;

    /*
      Attempt autoplay immediately.

      Modern browsers may block audible autoplay.
      If blocked, the first interaction starts
      the same cinematic audio clip.
    */

    window.setTimeout(
      startAudio,
      100
    );

    const interactionEvents = [
      "pointerdown",
      "touchstart",
      "keydown",
      "scroll"
    ];

    const unlock = function () {
      startAudio();

      interactionEvents.forEach(
        function (eventName) {
          window.removeEventListener(
            eventName,
            unlock
          );
        }
      );
    };

    interactionEvents.forEach(
      function (eventName) {
        window.addEventListener(
          eventName,
          unlock,
          {
            passive: true,
            once: false
          }
        );
      }
    );
  }

  /* =========================================================
     IMAGE MICRO-MOTION
     ========================================================= */

  function setupImageMotion() {
    if (reduceMotion) return;

    const images = root.querySelectorAll(
      ".koru-product-card .koru-image, " +
      ".koru-piece-card__image .koru-image, " +
      ".koru-community-gallery__item .koru-image"
    );

    images.forEach(function (image) {
      const card = image.closest(
        ".koru-product-card, " +
        ".koru-piece-card__image, " +
        ".koru-community-gallery__item"
      );

      if (!card) return;

      card.addEventListener(
        "pointermove",
        function (event) {
          const rect =
            card.getBoundingClientRect();

          const x =
            (event.clientX - rect.left) /
              rect.width -
            0.5;

          const y =
            (event.clientY - rect.top) /
              rect.height -
            0.5;

          image.style.transform =
            "scale(1.055) " +
            "translate3d(" +
            (x * 7).toFixed(2) +
            "px," +
            (y * 7).toFixed(2) +
            "px,0)";
        },
        {
          passive: true
        }
      );

      card.addEventListener(
        "pointerleave",
        function () {
          image.style.transform = "";
        }
      );
    });
  }

  /* =========================================================
     SMOOTH ANCHOR LINKS
     ========================================================= */

  function setupSmoothAnchorLinks() {
    const links = root.querySelectorAll(
      'a[href^="#"]'
    );

    links.forEach(function (link) {
      link.addEventListener(
        "click",
        function (event) {
          const href =
            link.getAttribute("href");

          if (
            !href ||
            href === "#"
          ) {
            return;
          }

          const target =
            document.querySelector(href);

          if (!target) return;

          event.preventDefault();

          target.scrollIntoView({
            behavior: reduceMotion
              ? "auto"
              : "smooth",

            block: "start"
          });
        }
      );
    });
  }

  /* =========================================================
     AUDIO VISIBILITY CONTROL
     ========================================================= */

  function setupVisibilityAudioPause() {
    if (!audio) return;

    const visibilityHandler =
      function () {
        if (document.hidden) {
          audio.pause();
        }
      };

    document.addEventListener(
      "visibilitychange",
      visibilityHandler
    );
  }

  /* =========================================================
     INITIALIZE
     ========================================================= */

  function init() {
    prepareHero();

    revealLoader();

    setupReveal();

    setupParallax();

    setupCounter();

    setupAudio();

    setupImageMotion();

    setupSmoothAnchorLinks();

    setupVisibilityAudioPause();
  }

  /* =========================================================
     DOM READY
     ========================================================= */

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      init,
      {
        once: true
      }
    );
  } else {
    init();
  }

  /* =========================================================
     SHOPIFY THEME EDITOR SUPPORT
     ========================================================= */

  document.addEventListener(
    "shopify:section:load",
    function (event) {
      if (!event.target) return;

      const sectionRoot =
        event.target.querySelector(
          "[data-koru-core]"
        );

      if (!sectionRoot) return;

      if (sectionRoot === root) {
        window.setTimeout(
          init,
          50
        );
      }
    }
  );

})();